import { z } from "zod";
import { RemediationOptionSchema } from "./policies.schema";

const insightsSummarySchema = z.object({
  criticalSeverityPolicyViolations: z.number(),
  highSeverityPolicyViolations: z.number(),
  mediumSeverityPolicyViolations: z.number(),
  lowSeverityPolicyViolations: z.number(),
  accessLevel: z.string().nullable(),
});

const applicationSchema = z.object({
  name: z.string(),
  environment: z.string(),
  insightsSummary: insightsSummarySchema,
});

const assetInformationSchema = z.object({
  name: z.string(),
  displayName: z.string(),
  arn: z.string(),
  description: z.string().nullable(),
  type: z.string(),
  nativeType: z.string(),
  source: z.string(),
  application: z.string(),
  tags: z.record(z.unknown()),
  savUrl: z.string(),
  isOpenToAllInternet: z.boolean().nullable(),
});

const ownershipSchema = z.object({
  name: z.string(),
  initials: z.string(),
  status: z.string(),
  source: z.array(z.string()),
  assignmentStatus: z.string(),
});

const relationshipsSchema = z.object({
  accounts: z.array(z.unknown()),
  credentials: z.array(z.unknown()),
});

const accessEntitlementSchema = z.object({
  role: z.string(),
  type: z.string(),
  inactiveFor90Days: z.boolean(),
  accessLevel: z.string().nullable(),
});

const assetMetadataSchema = z.object({
  tags: z.array(z.object({ key: z.string(), value: z.string() })),
  sshKeys: z.array(z.unknown()),
  mfaActive: z.boolean(),
  accessKeys: z.array(z.unknown()),
  createDate: z.string().datetime(),
  passwordMaxAge: z.number().nullable(),
  passwordEnabled: z.boolean(),
  passwordLastUsed: z.string().datetime().nullable(),
  passwordLastChanged: z.string().datetime().nullable(),
  signingCertificates: z.array(z.unknown()),
});

export const inventoryAssetDetailSchema = z.object({
  data: z.object({
    application: applicationSchema,
    assetInformation: assetInformationSchema,
    ownership: z.array(ownershipSchema),
    relationships: relationshipsSchema,
    accessEntitlements: z.array(accessEntitlementSchema),
    assetMetadata: assetMetadataSchema,
  }),
});

export type Application = z.infer<typeof applicationSchema>;
export type InsightsSummary = z.infer<typeof insightsSummarySchema>;
export type AssetInformation = z.infer<typeof assetInformationSchema>;
export type Ownership = z.infer<typeof ownershipSchema>;
export type AccessEntitlement = z.infer<typeof accessEntitlementSchema>;
export type AssetMetadata = z.infer<typeof assetMetadataSchema>;
export type Relationships = z.infer<typeof relationshipsSchema>;

export type InventoryAssetDetail = z.infer<typeof inventoryAssetDetailSchema>;

const findingItemSchema = z.object({
  policyName: z.string(),
  remediationOptions: z.array(RemediationOptionSchema),
  triggerPolicyId: z.string().uuid(),
});

export const findingsBySeveritySchema = z.object({
  critical: z.array(findingItemSchema),
  high: z.array(findingItemSchema),
  medium: z.array(findingItemSchema),
  low: z.array(findingItemSchema),
});

export const findingsBySeverityResponseSchema = z.object({
  data: z.object({
    critical: z.array(findingItemSchema),
    high: z.array(findingItemSchema),
    medium: z.array(findingItemSchema),
    low: z.array(findingItemSchema),
  }),
});

export type FindingItem = z.infer<typeof findingItemSchema>;
export type FindingsBySeverity = z.infer<typeof findingsBySeveritySchema>;
export type FindingsBySeverityResponse = z.infer<
  typeof findingsBySeverityResponseSchema
>;

export const assetTimelineFilterSchema = z.object({
  name: z.string(),
  operator: z.string(),
  values: z.array(z.string()).optional(),
  value: z.number().optional(),
});

export const eventTypeSchema = z.enum(["Create", "Delete", "Update"]);

export const accountSchema = z.object({
  requestId: z.string(),
  approvedBy: z.string(),
  businessJustification: z.string(),
  type: eventTypeSchema,
});

export const remediationEventSchema = z.object({
  remediationType: z.string(),
  remediationActionStatus: z.string().nullable(),
  externalIdHyperlink: z.string(),
  externalId: z.string(),
  reason: z.string(),
  targetSystem: z.string(),
});

export const ownerChangeSchema = z.object({
  requestId: z.string(),
  name: z.string(),
  approvedBy: z.string(),
  type: eventTypeSchema,
});

export const providerAccessChangeSchema = z.object({
  name: z.string(),
  sourceIPAddress: z.string(),
  userAgent: z.string(),
  accessEntitlementName: z.string(),
  type: eventTypeSchema,
});

export const accessChangeSchema = z.object({
  name: z.string(),
  requestId: z.string(),
  approvedBy: z.string(),
  businessJustification: z.string(),
  accessEntitlementName: z.string(),
  type: eventTypeSchema,
});

export const providerIdentityOperationSchema = z.object({
  name: z.string(),
  sourceIPAddress: z.string(),
  userAgent: z.string(),
  type: eventTypeSchema,
});

export const timelineEventItemSchema = z.object({
  eventId: z.string(),
  eventName: z.string(),
  eventType: z.string(),
  assetCategory: z.string(),
  initiatedBy: z.string(),
  initiatedThrough: z.string(),
  initiatedVia: z.string(),
  assetName: z.string(),
  eventTs: z.number(),
  ownerChange: ownerChangeSchema.optional(),
  providerAccessChange: providerAccessChangeSchema.optional(),
  accessChange: accessChangeSchema.optional(),
  providerIdentityOperation: providerIdentityOperationSchema.optional(),
  account: accountSchema.optional(),
  remediationEvent: remediationEventSchema,
});

export const timelineEventResponseSchema = z.object({
  value: z.array(timelineEventItemSchema),
  nextPageToken: z.string().nullable(),
});

export type TimelineEventItem = z.infer<typeof timelineEventItemSchema>;
export type TimelineEventResponse = z.infer<typeof timelineEventResponseSchema>;
export type EventType = z.infer<typeof eventTypeSchema>;
export type Account = z.infer<typeof accountSchema>;
export type RemediationEvent = z.infer<typeof remediationEventSchema>;
export type OwnerChange = z.infer<typeof ownerChangeSchema>;
export type ProviderAccessChange = z.infer<typeof providerAccessChangeSchema>;
export type AccessChange = z.infer<typeof accessChangeSchema>;
export type ProviderIdentityOperation = z.infer<
  typeof providerIdentityOperationSchema
>;
export type AssetTimelineFilter = z.infer<typeof assetTimelineFilterSchema>;

export const triggerSourceSchema = z.enum([
  "findings",
  "inventory",
  "policies",
]);
export type TriggerSource = z.infer<typeof triggerSourceSchema>;

export const ownerDataSchema = z.object({
  firstname: z.string(),
  lastname: z.string(),
  email: z.string(),
  managername: z.string(),
  username: z.string(),
  userkey: z.string(),
});

export const ownersListResponseSchema = z.object({
  value: z.array(ownerDataSchema),
  totalActiveUsers: z.number(),
  nextPageToken: z.string().nullable(),
});

export type OwnerData = z.infer<typeof ownerDataSchema>;
export type OwnersListResponse = z.infer<typeof ownersListResponseSchema>;

export const previouslyAssignedOwnersResponseSchema = z.object({
  value: z.array(
    z.object({
      username: z.string(),
      userkey: z.string(),
    }),
  ),
  nextPageToken: z.string().nullable(),
});

export type PreviouslyAssignedOwnersResponse = z.infer<
  typeof previouslyAssignedOwnersResponseSchema
>;

export const assignOwnerResponseSchema = z.object({
  message: z.string().optional(),
});

export type AssignOwnerResponse = z.infer<typeof assignOwnerResponseSchema>;

export const ownersFiltersValueResponseSchema = z.object({
  values: z.array(
    z.object({
      name: z.string(),
      id: z.string(),
    }),
  ),
  nextPageToken: z.string().nullable(),
});

export type OwnersFiltersValueResponse = z.infer<
  typeof ownersFiltersValueResponseSchema
>;

export const ownersfiltersListResponseSchema = z.object({
  data: z.array(
    z.object({
      name: z.string(),
      field: z.string(),
      show: z.boolean(),
    }),
  ),
});

export type OwnersFiltersListResponse = z.infer<
  typeof ownersfiltersListResponseSchema
>;
